<script setup lang="ts">
import type { DungeonEnvironment, DungeonRun, DungeonSeason } from '~/composables/charon'

const { charon } = useCharon()
const { t } = useI18n()

const props = defineProps<{
  leaderboardSize: number
  initialSeason: number
  seasons: DungeonSeason[]
  environments: DungeonEnvironment[]
}>()

const arenaPage = ref(0)
const arenaSeason = ref(props.initialSeason || props.seasons[0].number)
const arenaEnvironment = ref(props.environments[0].identifier)
const arenaPlayers = ref(1)

const { data: leaderboard } = useAsyncData(
  () => charon<DungeonRun[]>(
    `/dungeons/leaderboards/arena`,
    {
      params: {
        'season[eq]': arenaSeason.value,
        'players[size]': arenaPlayers.value,
        'environment[eq]': arenaEnvironment.value,
        'sort': ['finishedLevel[dec]', 'duration[asc]'],
        'page': arenaPage.value,
        'size': props.leaderboardSize,
      },
    },
  ),
  {
    watch: [arenaSeason, arenaPlayers, arenaEnvironment, arenaPage],
    lazy: false,
    server: true,
    transform: vs => vs?.map((v, i) => ({ ...v, place: i + 1 + arenaPage.value * props.leaderboardSize })) ?? null,
  },
)

const { data: maxPage } = useAsyncData(
  () => charon<number>(
    `/dungeons/leaderboards/arena/count`,
    {
      params: {
        'season[eq]': arenaSeason.value,
        'players[size]': arenaPlayers.value,
        'environment[eq]': arenaEnvironment.value,
      },
    },
  ),
  {
    watch: [arenaSeason, arenaPlayers, arenaEnvironment],
    transform: count => Math.ceil(count / props.leaderboardSize),
    lazy: false,
    server: true,
  },
)

// reset page on filter update
watch([arenaSeason, arenaPlayers, arenaEnvironment], () => arenaPage.value = 0)
</script>

<template>
  <DungeonRunLeaderboard
    v-model:page="arenaPage"
    :runs="leaderboard"
    :max-page="maxPage ?? 0"
    :size="leaderboardSize"
  >
    <div class="flex flex-col tablet:flex-row gap-2 justify-between">
      <select
        v-model="arenaSeason"
        class="bg-white text-slate-900 px-4 py-2 shadow-lg border border-gray-200 rounded-md hover:bg-slate-100 w-full tablet:w-auto"
      >
        <template
          v-for="season in props.seasons"
          :key="season.id"
        >
          <option
            :value="season.number"
          >
            {{ t(`jc.view.dungeon.leaderboard.season.label`, { season: season.number }) }}
          </option>
        </template>
      </select>
      <select
        v-model="arenaEnvironment"
        class="bg-white text-slate-900 px-4 py-2 shadow-lg border border-gray-200 rounded-md hover:bg-slate-100 w-full tablet:w-auto"
      >
        <template
          v-for="environment in props.environments"
          :key="environment.identifier"
        >
          <option
            :value="environment.identifier"
          >
            {{ t(`jc.dungeon.environment.${environment.identifier}.title`) }}
          </option>
        </template>
      </select>
      <select
        v-model="arenaPlayers"
        class="bg-white text-slate-900 px-4 py-2 shadow-lg border border-gray-200 rounded-md hover:bg-slate-100 w-full tablet:w-auto"
      >
        <template
          v-for="player in [1, 2, 3, 4]"
          :key="player"
        >
          <option
            :value="player"
          >
            {{ t(`jc.view.dungeon.leaderboard.players.label`, { players: player }) }}
          </option>
        </template>
      </select>
    </div>
  </DungeonRunLeaderboard>
</template>
